import React, { FC, useEffect, useState } from 'react'
import { styled } from '@moonpig/launchpad-utils'
import {
  ScreenReaderOnly,
  Text,
  VerticalStack,
} from '@moonpig/launchpad-components'
import {
  SelectorLegacy,
  SelectorLegacyContentWithLabel,
} from '@moonpig/launchpad-forms'
import { getVariantIcon } from '../../utils/getVariantIcon'

const StyledFieldset = styled.fieldset`
  padding: 0;
  margin: 0;
  border-width: 0px;
  text-align: left;
`

export type VariantSelectorItem = {
  label: string
  description?: string
  value: string
  price: string
  discountedPrice?: string
  absolutePrice?: string
  dimensions?:
    | {
        description: string
      }
    | string
    | null
}

export type VariantSelectorProps = {
  title?: string
  selectedValue: string
  items: VariantSelectorItem[]
  isLandscape: boolean
  onChange: (variantValue: string) => void
  hideInitialVariantSelection?: boolean
  setCheckedStatus?: (checked: boolean) => void
  titleIsScreenReaderOnly?: boolean
}

export const VariantSelector: FC<VariantSelectorProps> = ({
  title,
  selectedValue,
  items,
  isLandscape,
  onChange,
  hideInitialVariantSelection,
  setCheckedStatus,
  titleIsScreenReaderOnly = false,
}) => {
  const isInitialSizeTheSmallest = selectedValue === items[0].value
  const [hasBeenClicked, setHasBeenClicked] = useState(false)
  const shouldRenderChecked =
    hasBeenClicked || !hideInitialVariantSelection || !isInitialSizeTheSmallest

  useEffect(() => {
    if (setCheckedStatus) {
      setCheckedStatus(shouldRenderChecked)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const ConditionalScreenReaderWrapper = titleIsScreenReaderOnly
    ? ScreenReaderOnly
    : React.Fragment

  return (
    <StyledFieldset data-testid="variant-selector">
      <ConditionalScreenReaderWrapper>
        <Text
          data-testid="variant-select-title"
          as="legend"
          typography={{ xs: 'typeMobileDisplay03', md: 'typeDesktopDisplay05' }}
          color={'colorTextHeadline'}
          p="0"
          m="0"
          mb={4}
        >
          {title}
        </Text>
      </ConditionalScreenReaderWrapper>
      <VerticalStack spacing={3}>
        {items.map(
          ({
            label,
            description,
            price,
            value,
            discountedPrice,
            absolutePrice,
            dimensions,
          }) => {
            const screenReaderPrice = discountedPrice || absolutePrice || price

            const dimensionsLabel =
              typeof dimensions === 'string'
                ? dimensions
                : dimensions?.description

            const dimensionsScreenReaderLabel = dimensions
              ? `Dimensions: ${dimensionsLabel}`
              : null

            const screenReaderLabel = [
              label,
              screenReaderPrice,
              description,
              dimensionsScreenReaderLabel,
            ]
              .filter(labelPart => !!labelPart)
              .join(' - ')

            return (
              <SelectorLegacy
                key={label}
                type="radio"
                name="variant-selector"
                aria-describedby="validation-error"
                id={`variant-selector-${value}`}
                value={value}
                checked={shouldRenderChecked ? selectedValue === value : false}
                onChange={() => {
                  setHasBeenClicked(true)
                  onChange(value)
                }}
              >
                <SelectorLegacyContentWithLabel
                  aria-hidden="true"
                  headingText={
                    <>
                      <div
                        data-testid={`${label}-selector-heading`}
                        aria-hidden="true"
                      >
                        {label}
                      </div>
                      <ScreenReaderOnly
                        testId={`${label}-selector-screenreader-text`}
                      >
                        {screenReaderLabel}
                      </ScreenReaderOnly>
                    </>
                  }
                  bodyText={
                    <div
                      data-testid={`${label}-description`}
                      aria-hidden="true"
                    >
                      {description || ''}
                    </div>
                  }
                  labelText={
                    discountedPrice ? (
                      <>
                        <Text
                          data-testid={`${label}-discounted-price`}
                          typography="typeBodyLabel"
                          aria-hidden="true"
                        >
                          {discountedPrice}
                        </Text>{' '}
                        <Text
                          data-testid={`${label}-price`}
                          color="colorBlack80"
                          aria-hidden="true"
                        >
                          <s>{price}</s>
                        </Text>
                      </>
                    ) : (
                      <div data-testid={`${label}-price`} aria-hidden="true">
                        {price}
                      </div>
                    )
                  }
                  icon={getVariantIcon({ sizeId: value, isLandscape })}
                />
              </SelectorLegacy>
            )
          },
        )}
      </VerticalStack>
    </StyledFieldset>
  )
}
